import React, { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// Add new card images here — drop the WebP in /public and add the path
const CARD_TEXTURES = [
  '/card.webp',
  '/card2.webp',
  '/card3.webp',
  '/card4.webp',
  '/card5.webp',
  '/card6.webp',
];

function CameraController({ 
  isLastSlide, 
  onZoomComplete 
}: { 
  isLastSlide: boolean;
  onZoomComplete: (complete: boolean) => void;
}) {
  const { camera } = useThree();
  const targetZ = useRef(5);
  const targetY = useRef(0);
  const targetRotationX = useRef(0);
  
  useEffect(() => {
    targetZ.current = isLastSlide ? 12 : 5;
    targetY.current = isLastSlide ? -2 : 0;
    targetRotationX.current = 0; // Keep camera level/head-on
  }, [isLastSlide]);
  
  useFrame(() => {
    camera.position.z = THREE.MathUtils.lerp(camera.position.z, targetZ.current, 0.05);
    camera.position.y = THREE.MathUtils.lerp(camera.position.y, targetY.current, 0.05);
    camera.rotation.x = THREE.MathUtils.lerp(camera.rotation.x, targetRotationX.current, 0.05);
    
    // Check if zoom is complete (close enough to target)
    const isComplete = Math.abs(camera.position.z - targetZ.current) < 0.1;
    onZoomComplete(isComplete && isLastSlide);
  });
  
  return null;
}

function createRoundedRectShape(width: number, height: number, radius: number): THREE.Shape {
  const shape = new THREE.Shape();
  const w = width / 2;
  const h = height / 2;
  const r = radius;
  
  // Start from bottom-left corner + radius
  shape.moveTo(-w + r, -h);
  
  // Bottom edge
  shape.lineTo(w - r, -h);
  // Bottom-right corner
  shape.quadraticCurveTo(w, -h, w, -h + r);
  
  // Right edge  
  shape.lineTo(w, h - r);
  // Top-right corner
  shape.quadraticCurveTo(w, h, w - r, h);
  
  // Top edge
  shape.lineTo(-w + r, h);
  // Top-left corner
  shape.quadraticCurveTo(-w, h, -w, h - r);
  
  // Left edge
  shape.lineTo(-w, -h + r);
  // Bottom-left corner
  shape.quadraticCurveTo(-w, -h, -w + r, -h);
  
  return shape;
}

function Card({ 
  slideIndex, 
  position = [0, 0, 0], 
  rotation = [0, 0, 0],
  startInvisible = false,
  textureUrl = '/card.png'
}: { 
  slideIndex: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  startInvisible?: boolean;
  textureUrl?: string;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const texture = useLoader(THREE.TextureLoader, textureUrl);
  const spinAnimation = useRef(0);
  const targetRotation = useRef(rotation[1]);
  const previousIndex = useRef(0);

  const cardWidth = 2.125;
  const cardHeight = 3.375;
  const cardThickness = 0.015;
  const cornerRadius = 0.15;
  
  // Trigger spin when slide changes
  useEffect(() => {
    if (slideIndex !== previousIndex.current) {
      spinAnimation.current = Math.PI; // 180 degrees
      previousIndex.current = slideIndex;
    }
  }, [slideIndex]);

  // Create custom rounded rectangle geometry
  const geometry = useMemo(() => {
    const shape = createRoundedRectShape(cardWidth, cardHeight, cornerRadius);
    
    // Create extruded geometry from the shape
    const extrudeSettings = {
      depth: cardThickness,
      bevelEnabled: false,
      steps: 1
    };
    
    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // Center the geometry
    geom.center();
    
    // Fix UV mapping for the front and back faces
    const uvAttribute = geom.attributes.uv;
    const pos = geom.attributes.position;
    const normal = geom.attributes.normal;
    
    for (let i = 0; i < uvAttribute.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const nz = normal.getZ(i);
      
      // Only apply texture mapping to front and back faces, not edges
      if (Math.abs(nz) > 0.5) {
        // Check if this is a back face (negative z normal)
        if (nz < -0.5) {
          // Flip U coordinate for back face to mirror the image
          const u = 1.0 - ((x / cardWidth) + 0.5);
          const v = (y / cardHeight) + 0.5;
          uvAttribute.setXY(i, u, v);
        } else {
          // Regular mapping for front face
          const u = (x / cardWidth) + 0.5;
          const v = (y / cardHeight) + 0.5;
          uvAttribute.setXY(i, u, v);
        }
      } else {
        // For edge faces, use UV coordinates that will show white
        uvAttribute.setXY(i, 0, 0);
      }
    }
    
    uvAttribute.needsUpdate = true;
    
    return geom;
  }, [cardWidth, cardHeight, cardThickness, cornerRadius]);

  useFrame((state, delta) => {
    if (meshRef.current) {
      // Handle spin animation
      if (spinAnimation.current > 0) {
        const spinSpeed = 3; // Speed of rotation
        const spinAmount = Math.min(spinAnimation.current, spinSpeed * delta);
        targetRotation.current -= spinAmount; // Changed to subtract for opposite direction
        spinAnimation.current -= spinAmount;
      }
      
      // Apply rotation with gentle wobble
      meshRef.current.rotation.y = targetRotation.current + Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
    }
  });

  return (
    <group 
      ref={meshRef}
      position={position}
      rotation={[rotation[0], targetRotation.current + Math.sin(0) * 0.2, rotation[2]]}
    >
      {/* White core layer - slightly thinner */}
      <mesh geometry={geometry}>
        <meshStandardMaterial 
          color="#ffffff"
          metalness={0.1}
          roughness={0.9}
          side={THREE.DoubleSide}
          opacity={startInvisible ? 0 : 1}
          transparent={startInvisible}
        />
      </mesh>
      
      {/* Front textured face */}
      <mesh 
        geometry={geometry} 
        position={[0, 0, 0.008]}
      >
        <meshStandardMaterial 
          map={texture}
          metalness={0.1}
          roughness={0.9}
          side={THREE.FrontSide}
          opacity={startInvisible ? 0 : 1}
          transparent={startInvisible}
        />
      </mesh>
      
      {/* Back textured face */}
      <mesh 
        geometry={geometry} 
        position={[0, 0, -0.008]}
        rotation={[0, Math.PI, 0]}
      >
        <meshStandardMaterial 
          map={texture}
          metalness={0.1}
          roughness={0.9}
          side={THREE.FrontSide}
          opacity={startInvisible ? 0 : 1}
          transparent={startInvisible}
        />
      </mesh>
    </group>
  );
}


function FadingCard({ 
  slideIndex, 
  position, 
  shouldFade,
  delay = 0,
  textureUrl = '/card.png'
}: { 
  slideIndex: number;
  position: [number, number, number];
  shouldFade: boolean;
  delay?: number;
  textureUrl?: string;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const targetOpacity = useRef(0);
  const [initialized, setInitialized] = useState(false);
  const [delayComplete, setDelayComplete] = useState(false);
  
  useEffect(() => {
    if (shouldFade) {
      const timer = setTimeout(() => setDelayComplete(true), delay);
      return () => clearTimeout(timer);
    } else {
      setDelayComplete(false);
      targetOpacity.current = 0;
    }
  }, [shouldFade, delay]);
  
  useEffect(() => {
    targetOpacity.current = (shouldFade && delayComplete) ? 1 : 0;
  }, [shouldFade, delayComplete]);
  
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          // Set initial opacity to 0 on first frame
          if (!initialized) {
            child.material.opacity = 0;
            child.material.transparent = true;
            setInitialized(true);
          } else {
            // Much faster fade out, slower fade in
            const fadeSpeed = targetOpacity.current === 0 ? 0.2 : 0.08;
            child.material.opacity = THREE.MathUtils.lerp(
              child.material.opacity, 
              targetOpacity.current, 
              fadeSpeed
            );
          }
        }
      });
    }
  });
  
  return (
    <group ref={groupRef}>
      <Card slideIndex={slideIndex} position={position} startInvisible={true} textureUrl={textureUrl} />
    </group>
  );
}

function Card3D({ slideIndex = 0, isLastSlide = false }: { slideIndex?: number, isLastSlide?: boolean }) {
  const [zoomComplete, setZoomComplete] = useState(false);

  // Random card on each page load, stable for the session
  const [centerTextureIndex] = useState(() => Math.floor(Math.random() * CARD_TEXTURES.length));
  const centerTexture = CARD_TEXTURES[centerTextureIndex];

  // Gallery shows all textures except the center card
  const galleryTextures = useMemo(() => {
    return CARD_TEXTURES.filter((_, i) => i !== centerTextureIndex);
  }, [centerTextureIndex]);

  useEffect(() => {
    if (!isLastSlide) {
      setZoomComplete(false);
    }
  }, [isLastSlide]);

  const galleryPositions: [number, number, number][] = [
    [-2.5, 0, 0],
    [2.5, 0, 0],
    [-2.5, -4, 0],
    [0, -4, 0],
    [2.5, -4, 0],
  ];

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 45 }}
        gl={{ alpha: true, antialias: true }}
      >
        <CameraController
          isLastSlide={isLastSlide}
          onZoomComplete={setZoomComplete}
        />
        <ambientLight intensity={2.5} />
        <pointLight position={[10, 10, 10]} intensity={4} />
        <pointLight position={[-10, -10, -10]} intensity={3} />
        <pointLight position={[0, 5, 5]} intensity={2.5} />

        <Card slideIndex={slideIndex} position={[0, 0, 0]} textureUrl={centerTexture} />
        {galleryPositions.map((pos, i) => (
          <FadingCard
            key={galleryTextures[i]}
            slideIndex={slideIndex}
            position={pos}
            shouldFade={isLastSlide && zoomComplete}
            delay={(i + 1) * 100}
            textureUrl={galleryTextures[i]}
          />
        ))}

        <OrbitControls
          enabled={false}
        />
      </Canvas>
    </div>
  );
}

export default Card3D;